import { Box, Button, createStyles, Flex, TFCard, TFNotifier, Tooltip } from '@tf/ui';
import type React from 'react';
import { clientCaseKindLabels } from '@/components/cases/requestKind';
import { useCloseCaseMutation } from '@/core/api/caseRequests';
import type { CaseWithRequests } from '@/core/types/cases';
import { useGetAuthorName } from '@/core/utils';

import { CaseStatusBadge } from './CaseStatusBadge';
import { ClientEmail } from './ClientEmail';
import { InfoItem, InfoItems } from './InfoItems';

const useStyles = createStyles(() => {
	return {
		tooltipContent: {
			overflow: 'hidden',
			whiteSpace: 'nowrap',
			textOverflow: 'ellipsis',
		},
	};
});

export const CaseToolbar: React.FC<{ caseWithRequests: CaseWithRequests; paymentObjectId?: string }> = ({
	caseWithRequests,
	paymentObjectId,
}) => {
	const { classes } = useStyles();

	const getAuthorName = useGetAuthorName();
	const authorName = getAuthorName(caseWithRequests.case.meta['@author']);

	const canEditEmail = caseWithRequests.requests.every((r) => r.labels.status === 'DRAFT' && !r.labels.sent_at);

	const canCloseCase = caseWithRequests.requests.some((r) => r.labels.status === 'UPDATED');
	const closeClientCaseRequestMutation = useCloseCaseMutation();

	const closeCase = () => {
		closeClientCaseRequestMutation.mutate(
			{ caseId: caseWithRequests.case.id, paymentObjectId },
			{
				onSuccess: () => {
					TFNotifier.success('Case request closed successfully');
				},
				onError: () => {
					TFNotifier.error('Error while closing request');
				},
			},
		);
	};

	return (
		<TFCard innerPadding="0.5rem 1rem">
			<Flex align="center" justify="space-between">
				<InfoItems>
					<InfoItem label="Type">{clientCaseKindLabels[caseWithRequests.case.kind]}</InfoItem>
					<InfoItem label="Client email">
						<ClientEmail
							caseId={caseWithRequests.case.id}
							email={caseWithRequests.case.labels.recipient_email}
							isEditable={canEditEmail}
						/>
					</InfoItem>
					<InfoItem label="Author">
						<Tooltip label={authorName} position="top">
							<Box className={classes.tooltipContent}>{authorName}</Box>
						</Tooltip>
					</InfoItem>
					<InfoItem label="Status">
						<CaseStatusBadge
							caseStatus={caseWithRequests.case.labels.status}
							caseRequestStatus={caseWithRequests.requests[0].labels.status}
						/>
					</InfoItem>
				</InfoItems>

				{canCloseCase && caseWithRequests.case.labels.status === 'ACTIVE' ? (
					<Button variant="light" size="sm" disabled={closeClientCaseRequestMutation.isPending} onClick={closeCase}>
						Close case
					</Button>
				) : null}
			</Flex>
		</TFCard>
	);
};
